import { t, useLocale } from "@/i18n/locale";
import { useEffect, useId, useRef, useState } from "react";
import { computeConnectionsClient } from "@/api/computeConnectionsClient";
import type { PortablePlacement } from "@/api/portableApplicationClient";
import "./gpuPlacement.css";

type Options = Awaited<ReturnType<typeof computeConnectionsClient.list>>;
export function useGpuPlacement() {
  useLocale();
  const dialog = useRef<HTMLDialogElement>(null);
  const heading = useId();
  const pending = useRef<((value: PortablePlacement | null) => void) | null>(null);
  const [purpose, setPurpose] = useState<"formation" | undefined>();
  const [options, setOptions] = useState<Options | null>(null);
  useEffect(() => { if (options) dialog.current?.showModal(); }, [options]);
  useEffect(() => () => pending.current?.(null), []);
  const settle = (value: PortablePlacement | null) => pending.current?.(value);
  async function choose(signal: AbortSignal, purpose?: "formation"): Promise<PortablePlacement> {
    signal.throwIfAborted();
    const available = await computeConnectionsClient.list();
    signal.throwIfAborted();
    pending.current?.(null);
    setPurpose(purpose);
    return new Promise((resolve, reject) => {
      const abort = () => finish(null);
      const finish = (value: PortablePlacement | null) => {
        signal.removeEventListener("abort", abort);
        pending.current = null;
        dialog.current?.close();
        setOptions(null);
        if (value) resolve(value);
        else reject(new DOMException("GPU selection cancelled", "AbortError"));
      };
      pending.current = finish;
      signal.addEventListener("abort", abort, { once: true });
      setOptions(available);
    });
  }
  const connection = options?.connections.find(c => c.status === "active" && c.credential_health === "valid");
  const operator = options?.operator_runpod;
  return { choose, dialog: options ? (
    <dialog className="gpu-placement-dialog" ref={dialog} aria-labelledby={heading} onCancel={event => {event.preventDefault(); settle(null);}}>
      <h2 id={heading}>{t("Choose a GPU account")}</h2>
      <p>{purpose === "formation" ? t("Checking this app reserves a GPU until verification finishes. Starting it afterward requires a separate GPU choice.") : t("Starting the app reserves a GPU.")}</p>
      {operator?.available ? <button type="button" onClick={() => settle({kind:"managed",compute_account:"operator"})}>
        {t("Use Ato-provided RunPod")}
        <small>{t("Provided by Ato. GPU price limit {{price}}/hour, up to {{minutes}} minutes.", {price: `$${((operator.max_price_per_hour_cents ?? 0)/100).toFixed(2)}`, minutes: operator.max_instance_seconds ? Math.floor(operator.max_instance_seconds/60) : "—"})}</small>
      </button> : <p>{t("Ato-provided GPU is currently unavailable.")}</p>}
      {connection ? <button type="button" onClick={() => settle({kind:"managed",compute_account:"owner"})}>
        {t("Use my RunPod account")}
        <small>{t("Paid from your RunPod balance. GPU price limit {{price}}/hour, plus storage charges.", {price:`$${(connection.max_price_per_hour_cents/100).toFixed(2)}`})}</small>
      </button> : <a href="/settings/compute">{t("Connect my RunPod account")}</a>}
      <button type="button" onClick={() => settle(null)}>{t("Cancel")}</button>
    </dialog>
  ) : null };
}
